import { useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import EditIcon from '../../assets/icons/edit.svg?react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectHasPendingChanges,
  setEditMode,
  toggleEditMode
} from '../../store/application/applicationSlice';
import type { ChangeSummary } from '../../hooks/useYearPlan';
import { useHasPermission } from '../../hooks/useHasPermission';
import { PERMISSIONS } from '../../constants/permissions';
import { stripTags } from '../../utils/stringUtils';
import { Button } from '../UI/base/Button';
import { DiscardChangesModal } from './DiscardChangesModal';
import { EditActions } from './EditActions';
import { ReviewChangesModal } from './ReviewChangesModal';
import styles from './GanttEditToolbar.module.css';

interface GanttEditToolbarProps {
  isLocked?: boolean;
  lockedByCurrentUser?: boolean;
  lockedByName?: string | null;
  changeSummary?: ChangeSummary | null;
  onAcquireLock?: () => Promise<boolean>;
  onDiscardAll?: () => void | Promise<void>;
  onAcceptAll?: () => void | Promise<void>;
}

export const GanttEditToolbar = ({
  isLocked,
  lockedByCurrentUser,
  lockedByName,
  changeSummary,
  onAcquireLock,
  onDiscardAll,
  onAcceptAll
}: GanttEditToolbarProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isEditMode = useAppSelector((state) => state.applicationSlice.isEditMode);
  const hasUnsavedChanges = useAppSelector((state) => state.applicationSlice.hasUnsavedChanges);
  const canEditPlan = useHasPermission(PERMISSIONS.YearPlanWrite);
  // Plan edits and a typed-in year target, together. Only the review modal keeps
  // reading the plan's flag alone, since it summarises plan changes.
  const hasPendingChanges = useAppSelector(selectHasPendingChanges);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const handleEditClick = async () => {
    if (onAcquireLock) {
      const acquired = await onAcquireLock();
      if (!acquired) return;
    }
    // Nothing carried over from the last session: edit mode can be forced off
    // from outside (a lost lock tears it down) with a modal still open, and the
    // flag alone would pop that modal open again the moment editing resumes.
    setShowReviewModal(false);
    setShowDiscardModal(false);
    dispatch(setEditMode(true));
  };

  // The review modal states what changed about the PLAN, so it opens for plan
  // edits only. A target-only edit has nothing to list there and goes straight
  // through — Save still saves it, which is the whole point of the shared flag.
  const handleAccept = async () => {
    if (hasUnsavedChanges) {
      setShowReviewModal(true);
      return;
    }
    try {
      await onAcceptAll?.();
      dispatch(toggleEditMode());
    } catch {
      // Error is handled by the caller; stay in edit mode
    }
  };

  const discardAndExit = async () => {
    try {
      await onDiscardAll?.();
      dispatch(toggleEditMode());
    } catch {
      // Error is handled by the caller; stay in edit mode so the edits are still reachable
    }
  };

  // Discarding is unrecoverable, so pending edits get a confirmation. With nothing pending there is
  // nothing to lose, and the dialog would only be in the way of leaving edit mode.
  // A year target counts as pending: discarding throws it away too.
  const handleDiscard = async () => {
    if (hasPendingChanges) {
      setShowDiscardModal(true);
      return;
    }
    await discardAndExit();
  };

  const handleConfirmDiscard = async () => {
    setShowDiscardModal(false);
    await discardAndExit();
  };

  const handleConfirm = async () => {
    setShowReviewModal(false);
    try {
      await onAcceptAll?.();
      dispatch(toggleEditMode());
    } catch {
      // Error is handled by the caller; stay in edit mode
    }
  };

  // A lock whose holder cannot be resolved to a name is still a lock, so the name is not part of
  // the test — only of the wording.
  const isLockedByOther = Boolean(isLocked && !lockedByCurrentUser && !isEditMode);
  const lockedNoticeKey = lockedByName ? 'plan-locked-by' : 'plan-locked-by-unknown';

  // Announced rather than shown: the visible label and the locked notice carry no live-region
  // semantics of their own, and wrapping them in one would change the flex row's spacing. The
  // locked copy goes through the plain `t` (not <Trans>), so its <bold> markers are stripped
  // to keep a screen reader from reading them out. Leaving edit mode empties the region — no
  // copy exists for it, and inventing user-facing wording is not this ticket's call.
  const resolveStatusMessage = (): string => {
    if (isEditMode) return t('edit-plan');
    if (isLockedByOther) return stripTags(t(lockedNoticeKey, { name: lockedByName }));
    return '';
  };

  const statusMessage = resolveStatusMessage();

  // Edit mode replaces the button with a plain label; a user without write access
  // gets neither, since the plan is read-only to them.
  const renderEditControl = (): ReactNode => {
    if (isEditMode) return <span className={styles.editLabel}>{t('edit-plan')}</span>;
    if (!canEditPlan) return null;

    return (
      <Button
        type="primary"
        icon={<EditIcon className={styles.editIcon} />}
        disabled={isLocked && !lockedByCurrentUser}
        // Button is hidden once edit mode starts, so skip the auto-disable pending state.
        onClick={() => {
          void handleEditClick();
        }}
      >
        {t('edit-plan')}
      </Button>
    );
  };

  const editControl = renderEditControl();
  const toolbarCls = [styles.editToolbar, isEditMode && styles.editToolbarExpanded]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={toolbarCls}>
      <output className="visuallyHidden">{statusMessage}</output>
      {editControl}
      {isLockedByOther && (
        <span className={styles.lockedLabel}>
          {/* A lock whose holder cannot be resolved to a name is still a lock the user is about
              to be blocked by, so it is named as "another user" rather than left unsaid. */}
          <Trans
            i18nKey={lockedNoticeKey}
            values={lockedByName ? { name: lockedByName } : undefined}
            components={{ bold: <span className={styles.lockedName} /> }}
          />
        </span>
      )}
      <EditActions visible={isEditMode} onDiscard={handleDiscard} onAccept={handleAccept} />
      {/* Gated on edit mode, not just the flag: a forced exit — the scenario
          lock was lost — must not leave a Confirm up that would save or
          discard against a lock this tab no longer holds. */}
      {isEditMode && showDiscardModal && (
        <DiscardChangesModal
          onConfirm={handleConfirmDiscard}
          onClose={() => {
            setShowDiscardModal(false);
          }}
        />
      )}
      {isEditMode && showReviewModal && (
        <ReviewChangesModal
          changeSummary={changeSummary}
          onClose={() => {
            setShowReviewModal(false);
          }}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};
